/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import * as React from 'react';
import { Helmet } from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';
import he from 'he';
import { OgImage, OgImageData } from 'lib/types';

type MetaProps = JSX.IntrinsicElements['meta'];

type SEOProps = {
    description: string;
    lang: string;
    meta: MetaProps[];
    title: string;
    noIndex: boolean;
    ogImage?: OgImage;
    pathname?: string;
    ogType: string;
};

type SiteMetaData = {
    site: {
        siteMetadata: {
            title: string;
            description: string;
            siteUrl: string;
        };
    };
    defaultOgImage: OgImageData;
};

function getImageUrl({
    file: { publicURL, childImageSharp },
}: OgImage): string {
    if (childImageSharp) {
        const { resize, original } = childImageSharp;

        if (original.width < resize.width) {
            // don't use an increased size
            return original.src;
        }

        return resize.src;
    }

    return publicURL;
}

function getCanonical(siteUrl: string, pathname: string): string {
    const base = siteUrl?.endsWith('/') ? siteUrl.substring(0, -1) : siteUrl;
    const path = pathname.startsWith('/') ? pathname : `/${pathname}`;

    return base + path;
}

function getDefaultOgImage(imageFile: OgImageData): string {
    return (
        imageFile.childImageSharp?.resize.src ||
        imageFile.publicURL ||
        '/bond_vet_default.png'
    );
}

export const query = graphql`
    fragment OgImageData on File {
        publicURL
        childImageSharp {
            resize(fit: COVER, cropFocus: SOUTH, width: 1200, height: 630) {
                src
                width
            }
            original {
                src
                width
            }
        }
    }
    fragment OgImage on Upload {
        file {
            ...OgImageData
        }
    }
    fragment OgImageQuery on Query {
        ogImage: upload(imageId: { eq: $ogImage }) {
            ...OgImage
        }
    }
    fragment PageMetaData on Page {
        title
        description
    }
    query SiteMetaQuery {
        site {
            siteMetadata {
                title
                description
                siteUrl
            }
        }
        defaultOgImage: file(relativePath: { eq: "bond_vet_default.png" }) {
            ...OgImageData
        }
    }
`;

function SEO({
    description,
    lang,
    meta,
    title,
    noIndex,
    ogImage,
    pathname,
    ogType,
}: SEOProps): React.ReactElement {
    const {
        site: { siteMetadata },
        defaultOgImage,
    } = useStaticQuery<SiteMetaData>(query);

    const decodedTitle = `${he.decode(title || '')} | ${siteMetadata.title}`;
    const metaDescription = description || siteMetadata.description;

    const metaElements = [
        {
            name: 'description',
            content: metaDescription,
        },
        {
            property: 'og:title',
            content: decodedTitle,
        },
        {
            property: 'og:description',
            content: metaDescription,
        },
        {
            property: 'og:type',
            content: ogType,
        },
        {
            property: 'og:locale',
            content: 'en_US',
        },
        {
            property: 'og:site_name',
            content: siteMetadata.title,
        },
        {
            name: 'twitter:card',
            content: 'summary',
        },
        {
            name: 'twitter:title',
            content: decodedTitle,
        },
        {
            name: 'twitter:description',
            content: metaDescription,
        },
        ...meta,
    ];

    if (ogImage) {
        metaElements.push({
            property: 'og:image',
            content: getCanonical(siteMetadata.siteUrl, getImageUrl(ogImage)),
        });
    } else {
        metaElements.push({
            property: 'og:image',
            content: getDefaultOgImage(defaultOgImage),
        });
    }

    if (noIndex) {
        metaElements.push({
            name: 'robots',
            content: 'noindex',
        });
    } else {
        metaElements.push({
            name: 'robots',
            content:
                'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
        });
    }

    const canonical = pathname && getCanonical(siteMetadata.siteUrl, pathname);
    if (canonical) {
        metaElements.push({
            property: 'og:url',
            content: canonical,
        });
    }

    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
            title={decodedTitle}
            link={
                canonical
                    ? [{ rel: 'canonical', key: canonical, href: canonical }]
                    : []
            }
            meta={metaElements}
        />
    );
}

SEO.defaultProps = {
    lang: 'en',
    meta: [],
    description: '',
    noIndex: false,
    ogType: 'website',
};

export default SEO;
